import { useCallback, useState, MouseEvent, useEffect, useRef } from "react";
import { Colors } from "../../../utils/colors";
import { Editor } from "@tiptap/react";
import RectangleButton from "../../Buttons/RectangleButton";
import { Icon } from "@iconify/react";
import CustomTextField from "../../CustomTextField";
import { DarkTheme, useTheme } from "../../../hooks/useTheme";
import { ThemeType } from "@markit/common.types";

type InsertInlineLinkProps = {
  editor: Editor;
  currentLinkData: {
    href: string;
    text: string;
    hasFormatting: boolean;
  } | null;
  setCurrentLinkData: (
    data: {
      href: string;
      text: string;
      hasFormatting: boolean;
    } | null
  ) => void;
  showLinkManagementPopup: boolean;
  setShowLinkManagementPopup: (show: boolean) => void;
  modalPosition: { x: number; y: number };
  setModalPosition: (position: { x: number; y: number }) => void;
  pressLinkButton: boolean;
  setPressLinkButton: (press: boolean) => void;
  eventTheme?: ThemeType;
};

const InsertInlineLink = (props: InsertInlineLinkProps) => {
  const {
    editor,
    currentLinkData,
    setCurrentLinkData,
    showLinkManagementPopup,
    setShowLinkManagementPopup,
    modalPosition,
    setModalPosition,
    pressLinkButton,
    setPressLinkButton,
    eventTheme,
  } = props;
  const { theme } = useTheme(eventTheme, eventTheme === undefined);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [tempLinkData, setTempLinkData] = useState<{
    href: string;
    text: string;
    hasFormatting: boolean;
  }>({ href: "", text: "", hasFormatting: false });
  const [errorText, setErrorText] = useState<{
    message: string;
    type: "text" | "url" | "none";
  }>({ message: "", type: "none" });

  const styles = {
    popupContainer: {
      minWidth: 308,
      backgroundColor: theme.SecondaryBG.backgroundColor,
      padding: 14,
      borderRadius: 20,
      border: `0.5px solid ${Colors.GRAY2}`,
      transform: "none",
    },
  };

  // Helper function to calculate boundary-safe transform
  const getBoundarySafeTransform = useCallback(
    (position: { x: number; y: number }, width: number, height: number) => {
      let transformX = 0;
      let transformY = 0;

      // Check horizontal boundaries - add more padding to prevent cutoff
      if (position.x + width > window.innerWidth - 40) {
        transformX = window.innerWidth - position.x - width - 40;
      }

      // Check vertical boundaries
      if (position.y + height > window.innerHeight - 40) {
        transformY = window.innerHeight - position.y - height - 40;
      }

      // Ensure modal doesn't go off the left edge
      if (position.x + transformX < 40) {
        transformX = 40 - position.x;
      }

      // Ensure modal doesn't go off the top edge
      if (position.y + transformY < 40) {
        transformY = 40 - position.y;
      }

      return `translate(${transformX}px, ${transformY}px)`;
    },
    []
  );

  const ref = useRef<any>(null);
  useEffect(() => {
    const handler = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowLinkModal(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  // Helper function to calculate modal position based on cursor
  const calculateModalPosition = useCallback(() => {
    const { state } = editor;
    const { selection } = state;
    const coords = editor.view.coordsAtPos(selection.from);

    const modalX = coords.left; // Align to left edge of cursor
    const modalY = coords.bottom + 10; // 10px below cursor

    setModalPosition({ x: modalX, y: modalY });
  }, [editor, setModalPosition]);

  // Handle window resize to reposition modals
  useEffect(() => {
    const handleResize = () => {
      if (showLinkModal || showLinkManagementPopup) {
        calculateModalPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [showLinkModal, showLinkManagementPopup, calculateModalPosition]);

  const handleLinkButtonClick = useCallback(() => {
    setErrorText({ message: "", type: "none" });

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");

    // Check for any formatting in the selection range
    let hasFormatting = false;
    if (from !== to) {
      editor.state.doc.nodesBetween(from, to, (node) => {
        if (node.marks && node.marks.length > 0) {
          hasFormatting = true;
          return false;
        }
      });
    }

    // Calculate modal position
    calculateModalPosition();

    // If there's selected text, use it as the link text
    // If no text is selected, start with empty fields
    setTempLinkData({ text: selectedText, href: "", hasFormatting });
    setShowLinkModal(true);
  }, [editor, calculateModalPosition]);

  useEffect(() => {
    if (pressLinkButton) {
      handleLinkButtonClick();
      setPressLinkButton(false);
    }
  }, [pressLinkButton, handleLinkButtonClick, setPressLinkButton]);

  const handleInsertLink = useCallback(() => {
    // Trim values once to avoid repeated operations
    const trimmedText = tempLinkData.text.trim();
    const trimmedUrl = tempLinkData.href.trim();

    // Return early if no changes were made (for editing existing links)
    if (currentLinkData) {
      const noChanges =
        currentLinkData.href === trimmedUrl &&
        currentLinkData.text === trimmedText;
      if (noChanges) {
        setShowLinkModal(false);
        setTempLinkData({ href: "", text: "", hasFormatting: false });
        setErrorText({ message: "", type: "none" });
        return;
      }
    }

    if (!trimmedText && !tempLinkData.hasFormatting) {
      setErrorText({ message: "Please enter text here.", type: "text" });
      return;
    }

    if (!trimmedUrl) {
      setErrorText({ message: "Please enter a valid URL here.", type: "url" });
      return;
    }

    // Add protocol if not present
    let url = trimmedUrl;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    // Check if there's selected text in the editor
    const { from, to } = editor.state.selection;
    const hasSelection = !editor.isEmpty && from !== to;

    if (currentLinkData) {
      // 1. Updating existing link and text
      if (tempLinkData.hasFormatting) {
        // 1.1. There is formatting on the selected text, only apply the link to the selected text
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url, target: "_blank" })
          .run();
      } else {
        // 1.2. There is no formatting on the selected text, apply the link to the selected text
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .insertContent(`<a href="${url}">${trimmedText}</a>`)
          .setLink({ href: url, target: "_blank" })
          .run();
      }
    } else if (hasSelection) {
      // 2. Inserting new link where there is highlighted text
      if (tempLinkData.hasFormatting) {
        // 2.1. There is formatting on the selected text, only apply the link to the selected text
        editor.chain().focus().setLink({ href: url, target: "_blank" }).run();
      } else {
        // 2.2. There is no formatting on the selected text, apply the link to the selected text
        editor
          .chain()
          .focus()
          .deleteRange({ from, to })
          .setLink({ href: url, target: "_blank" })
          .command(({ tr }) => {
            tr.insertText(trimmedText);
            return true;
          })
          .run();
      }
    } else {
      // 3. Inserting new link where there is no highlighted text (single position)
      editor
        .chain()
        .focus(editor.isEmpty ? "end" : undefined)
        .setLink({ href: url })
        .command(({ tr }) => {
          tr.insertText(trimmedText);
          return true;
        })
        .run();
    }

    setShowLinkModal(false);
    setTempLinkData({ href: "", text: "", hasFormatting: false });
  }, [editor, currentLinkData, tempLinkData]);

  const handleOpenLink = useCallback(() => {
    if (currentLinkData?.href) {
      window.open(currentLinkData.href, "_blank");
    }
    setShowLinkManagementPopup(false);
  }, [currentLinkData, setShowLinkManagementPopup]);

  const handleChangeLink = useCallback(() => {
    if (currentLinkData) {
      setTempLinkData(currentLinkData);
      setShowLinkManagementPopup(false);
      setShowLinkModal(true);
    }
  }, [currentLinkData, setShowLinkManagementPopup, setShowLinkModal]);

  const handleRemoveExistingLink = useCallback(() => {
    editor.chain().focus().unsetLink().run();
    setShowLinkManagementPopup(false);
    setCurrentLinkData(null);
  }, [editor, setCurrentLinkData, setShowLinkManagementPopup]);

  const renderOptionButton = useCallback(
    (title: string, onPress: () => void, iconName: string) => (
      <div>
        <RectangleButton
          buttonLabel={title}
          onPress={onPress}
          altColor={theme.SecondaryBG.backgroundColor}
          altTextColor={theme.PrimaryText.color}
          iconLeft={
            <Icon
              icon={iconName}
              height={14}
              style={{ color: theme.PrimaryText.color }}
            />
          }
          containerStyles={{
            padding: 10,
            borderRadius: 8,
            border: `0.5px solid ${
              theme === DarkTheme ? Colors.BLACK1 : Colors.GRAY2
            }`,
          }}
        />
      </div>
    ),
    [theme]
  );

  return (
    <div>
      {renderOptionButton("Link", handleLinkButtonClick, "mdi:link-variant")}
      {showLinkModal ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
          }}
        >
          <div
            ref={ref}
            style={{
              ...styles.popupContainer,
              position: "fixed",
              left: modalPosition.x,
              top: modalPosition.y,
              transform: getBoundarySafeTransform(modalPosition, 320, 200),
            }}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodyMedium" style={theme.PrimaryText}>
                {currentLinkData ? "Edit Link" : "Create a link"}
              </span>
              <div className="ColumnNormal" style={{ gap: 10 }}>
                {!tempLinkData.hasFormatting ? (
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <CustomTextField
                      value={tempLinkData.text}
                      placeholder="Enter Text..."
                      inputMode="text"
                      onChange={(e) => {
                        setTempLinkData({
                          ...tempLinkData,
                          text: e.target.value,
                        });
                        if (errorText.type === "text") {
                          setErrorText({ message: "", type: "none" });
                        }
                      }}
                      noAutocomplete
                      altPadding="14px"
                      autoFocus
                      error={errorText.type === "text"}
                      eventTheme={eventTheme || ThemeType.Light}
                    />
                    {errorText.type === "text" ? (
                      <span
                        className="bodySmallRegular"
                        style={{ color: Colors.RED3 }}
                      >
                        {errorText.message}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                <div className="ColumnNormal" style={{ gap: 7 }}>
                  <CustomTextField
                    value={tempLinkData.href}
                    placeholder="Type or paste link..."
                    inputMode="text"
                    onChange={(e) => {
                      setTempLinkData({
                        ...tempLinkData,
                        href: e.target.value,
                      });
                      if (errorText.type === "url") {
                        setErrorText({ message: "", type: "none" });
                      }
                    }}
                    noAutocomplete
                    altPadding="14px"
                    error={errorText.type === "url"}
                    eventTheme={eventTheme || ThemeType.Light}
                  />
                  {errorText.type === "url" ? (
                    <span
                      className="bodySmallRegular"
                      style={{ color: Colors.RED3 }}
                    >
                      {errorText.message}
                    </span>
                  ) : null}
                </div>
                <div className="AlignedRow" style={{ gap: 12 }}>
                  <RectangleButton
                    buttonLabel="Apply"
                    onPress={handleInsertLink}
                    altColor={theme.ButtonBG.backgroundColor}
                    altTextColor={theme.ButtonText.color}
                    width={67}
                    containerStyles={{ padding: 14 }}
                  />
                  <RectangleButton
                    buttonLabel="Cancel"
                    onPress={() => setShowLinkModal(false)}
                    altColor={theme.NewPrimaryBG.backgroundColor}
                    altTextColor={theme.PrimaryText.color}
                    width={75}
                    containerStyles={{
                      padding: 14,
                      border: `0.5px solid ${Colors.GRAY2}`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {/* Link Management Popup */}
      {showLinkManagementPopup && currentLinkData ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
          }}
          onClick={() => setShowLinkManagementPopup(false)}
        >
          <div
            className="AlignedRowSpaced"
            style={{
              ...styles.popupContainer,
              maxWidth: 408,
              gap: 14,
              paddingBlock: 10,
              position: "fixed",
              left: modalPosition.x,
              top: modalPosition.y,
              transform: getBoundarySafeTransform(modalPosition, 360, 60),
            }}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <span
              className="TextOverflowEllipsis"
              style={{
                textDecoration: "underline",
                fontSize: 14,
                cursor: "pointer",
                maxWidth: 300,
                ...theme.PrimaryText,
              }}
              onClick={handleOpenLink}
            >
              {currentLinkData.href}
            </span>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <RectangleButton
                buttonLabel="Change"
                onPress={handleChangeLink}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                width={80}
                containerStyles={{ padding: 14 }}
              />
              <RectangleButton
                buttonLabel="Remove"
                onPress={handleRemoveExistingLink}
                altColor={Colors.WHITE1}
                altTextColor={Colors.RED3}
                width={80}
                containerStyles={{
                  padding: 14,
                  border: `0.5px solid ${Colors.GRAY2}`,
                }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InsertInlineLink;
